import React, { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import DialogModal from '../components/DialogModal';
import faqService from '../services/faqService';
import { useAuth } from '../context/AuthContext';
import {
  MagnifyingGlass,
  Plus,
  PencilSimple,
  Trash,
  CaretDown,
  CaretUp,
  Eye,
  CheckCircle,
  XCircle,
} from '@phosphor-icons/react';

const ADMIN_ROLES = ['super_admin', 'school_admin', 'hr'];

const EMPTY_FORM = { category: '', question: '', answer: '', is_published: true };

function FaqPage() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const [expandedIds, setExpandedIds] = useState(new Set());
  const viewedRef = useRef(new Set());

  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [savingEdit, setSavingEdit] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState(null);

  const loadCategories = useCallback(() => {
    faqService.listCategories()
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch((err) => toast.error(err.message || 'Failed to load FAQ categories'));
  }, []);

  const loadArticles = useCallback(() => {
    setLoading(true);
    faqService.list(selectedCategory || undefined)
      .then((data) => setArticles(Array.isArray(data) ? data : []))
      .catch((err) => toast.error(err.message || 'Failed to load FAQ articles'))
      .finally(() => setLoading(false));
  }, [selectedCategory]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { loadArticles(); }, [loadArticles]);

  const filteredArticles = articles.filter((a) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (a.question || '').toLowerCase().includes(q) || (a.answer || '').toLowerCase().includes(q);
  });

  const toggleExpand = (article) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(article.id)) {
        next.delete(article.id);
      } else {
        next.add(article.id);
        if (!viewedRef.current.has(article.id)) {
          viewedRef.current.add(article.id);
          faqService.recordView(article.id)
            .then((result) => {
              setArticles((list) => list.map((a) => (a.id === article.id ? { ...a, view_count: result.view_count } : a)));
            })
            .catch(() => {});
        }
      }
      return next;
    });
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!addForm.category.trim() || !addForm.question.trim() || !addForm.answer.trim()) {
      toast.error('Category, question and answer are required');
      return;
    }
    setSubmitting(true);
    try {
      await faqService.create(addForm);
      toast.success('FAQ article created');
      setAddForm(EMPTY_FORM);
      setShowAddForm(false);
      loadCategories();
      loadArticles();
    } catch (error) {
      toast.error(error.message || 'Failed to create FAQ article');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (article) => {
    setEditingId(article.id);
    setEditForm({
      category: article.category || '',
      question: article.question || '',
      answer: article.answer || '',
      is_published: !!article.is_published,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(EMPTY_FORM);
  };

  const handleEditSave = async (id) => {
    if (!editForm.category.trim() || !editForm.question.trim() || !editForm.answer.trim()) {
      toast.error('Category, question and answer are required');
      return;
    }
    setSavingEdit(true);
    try {
      await faqService.update(id, editForm);
      toast.success('FAQ article updated');
      cancelEdit();
      loadCategories();
      loadArticles();
    } catch (error) {
      toast.error(error.message || 'Failed to update FAQ article');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleTogglePublish = async (article) => {
    try {
      await faqService.update(article.id, { is_published: !article.is_published });
      toast.success(article.is_published ? 'Article unpublished' : 'Article published');
      loadArticles();
    } catch (error) {
      toast.error(error.message || 'Failed to update publish status');
    }
  };

  const handleDelete = (article) => {
    setConfirmDialog({
      title: 'Delete FAQ Article',
      message: `Delete "${article.question}"? This cannot be undone.`,
      type: 'error',
      confirmLabel: 'Delete',
      onConfirm: async () => {
        try {
          await faqService.remove(article.id);
          toast.success('FAQ article deleted');
          loadCategories();
          loadArticles();
        } catch (error) {
          toast.error(error.message || 'Failed to delete FAQ article');
        }
      },
    });
  };

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>FAQ / Knowledge Base</h1>
            <p>Browse frequently asked questions{isAdmin ? ', and manage articles below.' : '.'}</p>
          </div>
          {isAdmin && (
            <button className="btn btn-primary" onClick={() => setShowAddForm((v) => !v)}>
              <Plus size={16} /> {showAddForm ? 'Cancel' : 'Add FAQ'}
            </button>
          )}
        </div>

        {isAdmin && showAddForm && (
          <div className="card" style={{ padding: 20, marginBottom: 20 }}>
            <h3 style={{ marginTop: 0 }}>New FAQ Article</h3>
            <form onSubmit={handleAddSubmit} style={{ display: 'grid', gap: 12 }}>
              <label style={{ fontSize: 13 }}>
                Category
                <input
                  type="text"
                  required
                  value={addForm.category}
                  onChange={(e) => setAddForm({ ...addForm, category: e.target.value })}
                  list="faq-category-options"
                  style={{ width: '100%', padding: 8, marginTop: 4, borderRadius: 6, border: '1px solid #d1d5db' }}
                />
                <datalist id="faq-category-options">
                  {categories.map((c) => <option key={c} value={c} />)}
                </datalist>
              </label>
              <label style={{ fontSize: 13 }}>
                Question
                <input
                  type="text"
                  required
                  value={addForm.question}
                  onChange={(e) => setAddForm({ ...addForm, question: e.target.value })}
                  style={{ width: '100%', padding: 8, marginTop: 4, borderRadius: 6, border: '1px solid #d1d5db' }}
                />
              </label>
              <label style={{ fontSize: 13 }}>
                Answer
                <textarea
                  required
                  rows={4}
                  value={addForm.answer}
                  onChange={(e) => setAddForm({ ...addForm, answer: e.target.value })}
                  style={{ width: '100%', padding: 8, marginTop: 4, borderRadius: 6, border: '1px solid #d1d5db' }}
                />
              </label>
              <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="checkbox"
                  checked={addForm.is_published}
                  onChange={(e) => setAddForm({ ...addForm, is_published: e.target.checked })}
                />
                Published
              </label>
              <div>
                <button className="btn btn-primary" type="submit" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Create Article'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 1fr) 3fr', gap: 24, alignItems: 'start' }}>
          <div className="card" style={{ padding: 16 }}>
            <h4 style={{ marginTop: 0 }}>Categories</h4>
            <div style={{ display: 'grid', gap: 4 }}>
              <button
                className={`btn btn-sm ${!selectedCategory ? 'btn-primary' : 'btn-ghost'}`}
                style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                onClick={() => setSelectedCategory('')}
              >
                All Categories
              </button>
              {categories.map((c) => (
                <button
                  key={c}
                  className={`btn btn-sm ${selectedCategory === c ? 'btn-primary' : 'btn-ghost'}`}
                  style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                  onClick={() => setSelectedCategory(c)}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="card" style={{ padding: 16, marginBottom: 16 }}>
              <div style={{ position: 'relative' }}>
                <MagnifyingGlass size={16} style={{ position: 'absolute', left: 10, top: 10, color: '#9ca3af' }} />
                <input
                  type="text"
                  placeholder="Search FAQs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ width: '100%', padding: '8px 8px 8px 32px', borderRadius: 6, border: '1px solid #d1d5db' }}
                />
              </div>
            </div>

            {loading ? (
              <div className="loading">Loading...</div>
            ) : filteredArticles.length === 0 ? (
              <div className="card" style={{ padding: 20 }}>
                <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>No FAQ articles found.</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                {filteredArticles.map((article) => {
                  const isExpanded = expandedIds.has(article.id);
                  const isEditing = editingId === article.id;
                  return (
                    <div key={article.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                      <button
                        onClick={() => toggleExpand(article)}
                        style={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '14px 16px',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 12, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>{article.category}</div>
                          <div style={{ fontWeight: 600, marginTop: 2 }}>{article.question}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                          {isAdmin && !article.is_published && (
                            <span style={{ fontSize: 11, color: '#d97706', fontWeight: 600 }}>DRAFT</span>
                          )}
                          <span style={{ fontSize: 12, color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Eye size={14} /> {article.view_count ?? 0}
                          </span>
                          {isExpanded ? <CaretUp size={16} /> : <CaretDown size={16} />}
                        </div>
                      </button>

                      {isExpanded && (
                        <div style={{ padding: '0 16px 16px', borderTop: '1px solid #f0f0f0' }}>
                          {isEditing ? (
                            <div style={{ display: 'grid', gap: 10, paddingTop: 12 }}>
                              <label style={{ fontSize: 13 }}>
                                Category
                                <input
                                  type="text"
                                  value={editForm.category}
                                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                                  style={{ width: '100%', padding: 8, marginTop: 4, borderRadius: 6, border: '1px solid #d1d5db' }}
                                />
                              </label>
                              <label style={{ fontSize: 13 }}>
                                Question
                                <input
                                  type="text"
                                  value={editForm.question}
                                  onChange={(e) => setEditForm({ ...editForm, question: e.target.value })}
                                  style={{ width: '100%', padding: 8, marginTop: 4, borderRadius: 6, border: '1px solid #d1d5db' }}
                                />
                              </label>
                              <label style={{ fontSize: 13 }}>
                                Answer
                                <textarea
                                  rows={4}
                                  value={editForm.answer}
                                  onChange={(e) => setEditForm({ ...editForm, answer: e.target.value })}
                                  style={{ width: '100%', padding: 8, marginTop: 4, borderRadius: 6, border: '1px solid #d1d5db' }}
                                />
                              </label>
                              <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <input
                                  type="checkbox"
                                  checked={editForm.is_published}
                                  onChange={(e) => setEditForm({ ...editForm, is_published: e.target.checked })}
                                />
                                Published
                              </label>
                              <div style={{ display: 'flex', gap: 8 }}>
                                <button className="btn btn-primary btn-sm" disabled={savingEdit} onClick={() => handleEditSave(article.id)}>
                                  {savingEdit ? 'Saving...' : 'Save'}
                                </button>
                                <button className="btn btn-secondary btn-sm" onClick={cancelEdit}>Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p style={{ fontSize: 14, color: '#374151', whiteSpace: 'pre-wrap', paddingTop: 12 }}>{article.answer}</p>
                              {isAdmin && (
                                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                                  <button className="btn btn-sm btn-ghost" onClick={() => startEdit(article)} title="Edit">
                                    <PencilSimple size={14} /> Edit
                                  </button>
                                  <button className="btn btn-sm btn-ghost" onClick={() => handleTogglePublish(article)} title="Toggle publish">
                                    {article.is_published ? <XCircle size={14} /> : <CheckCircle size={14} />}
                                    {article.is_published ? ' Unpublish' : ' Publish'}
                                  </button>
                                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(article)} title="Delete">
                                    <Trash size={14} /> Delete
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <DialogModal
        isOpen={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        type={confirmDialog?.type || 'warning'}
        primaryAction={{ label: confirmDialog?.confirmLabel || 'Confirm', onClick: () => confirmDialog?.onConfirm() }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </Layout>
  );
}

export default FaqPage;
